import { ReactNode, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from "@/lib/router-compat";
import { motion } from 'framer-motion';
import { Menu, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import Logo from '@/components/Logo';
import Footer from '@/components/Footer';
import BackButton from '@/components/BackButton';
import { CookieConsent } from '@/components/CookieConsent';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { PremiumCTA } from '@/components/cta/PremiumCTA';

interface PublicLayoutProps {
  children: ReactNode;
  showFooter?: boolean;
}

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5, ease: "easeOut" as const } },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } },
};

const mainNavLinks = [
  { href: '/events', labelKey: 'nav.experience' },
  { href: '/locations', labelKey: 'nav.locations' },
  { href: '/pricing', labelKey: 'nav.pricing' },
  { href: '/journal', labelKey: 'nav.journal' },
  { href: '/insights', labelKey: 'nav.insights' },
];

const opportunityLinks = [
  { href: '/become-curator', labelKey: 'nav.become_curator' },
  { href: '/become-affiliate', labelKey: 'nav.affiliate_program' },
  { href: '/partner', labelKey: 'nav.venue_chef_partners' },
  { href: '/partner-with-us', labelKey: 'nav.participation_overview' },
];

export const PublicLayout = ({ children, showFooter = true }: PublicLayoutProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isActive = (href: string) => location.pathname === href;
  const isOpportunityActive = opportunityLinks.some(l => isActive(l.href));

  const handleMobileNav = useCallback((path: string) => {
    setMobileMenuOpen(false);
    setTimeout(() => navigate(path), 50);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-soft flex flex-col">
      {/* Floating capsule header */}
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center px-3 pt-3 md:pt-4">
        <motion.nav
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-6xl rounded-full bg-secondary/95 backdrop-blur-xl border border-border/30 shadow-elevated px-3 md:px-5"
        >
          <div className="flex items-center justify-between h-12 md:h-[52px]">
            {/* Logo */}
            <div className="flex items-center gap-2 shrink-0">
              {location.pathname !== '/' && <BackButton />}
              <Link to="/"><Logo size="lg" /></Link>
            </div>

            {/* Desktop Navigation — gold text on purple capsule */}
            <motion.div
              className="hidden lg:flex items-center gap-6 xl:gap-8"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.2 } }
              }}
            >
              {mainNavLinks.map((link) => (
                <motion.div key={link.href} variants={fadeIn}>
                  <Link
                    to={link.href}
                    className={`text-[13px] font-semibold tracking-wide uppercase transition-colors ${
                      isActive(link.href) ? 'text-primary' : 'text-primary/70 hover:text-primary'
                    }`}
                  >
                    {t(link.labelKey)}
                  </Link>
                </motion.div>
              ))}

              {/* Opportunities Dropdown */}
              <motion.div variants={fadeIn}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className={`flex items-center gap-1 text-[13px] font-semibold tracking-wide uppercase transition-colors ${
                      isOpportunityActive ? 'text-primary' : 'text-primary/70 hover:text-primary'
                    }`}>
                      {t('nav.participation')}
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-56">
                    {opportunityLinks.map((link) => (
                      <DropdownMenuItem key={link.href} asChild>
                        <Link to={link.href} className="w-full cursor-pointer">{t(link.labelKey)}</Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Link
                  to="/learn-more"
                  className={`text-[13px] font-semibold tracking-wide uppercase transition-colors ${
                    isActive('/learn-more') ? 'text-primary' : 'text-primary/70 hover:text-primary'
                  }`}
                >
                  {t('nav.about')}
                </Link>
              </motion.div>

              {/* Language Switcher */}
              <motion.div variants={fadeIn}>
                <LanguageSwitcher variant="compact" />
              </motion.div>
            </motion.div>

            {/* Desktop Action buttons */}
            <motion.div
              className="hidden lg:flex items-center gap-2 shrink-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Link to="/auth">
                <Button variant="ghost" className="rounded-full px-4 h-8 text-[12px] font-semibold text-primary/80 hover:text-primary hover:bg-primary/10 transition-all">
                  {t('nav.log_in')}
                </Button>
              </Link>
              <Link to="/auth">
                <Button variant="luxury" className="rounded-full px-5 h-9 text-[12px] font-semibold">
                  {t('nav.join_amorette')}
                </Button>
              </Link>
            </motion.div>

            {/* Mobile Navigation */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                  <Button variant="ghost" size="icon" className="text-primary/80 hover:text-primary hover:bg-primary/10">
                    <Menu className="h-5 w-5" />
                  </Button>
                </motion.div>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] pt-12">
                <motion.nav
                  className="flex flex-col gap-5"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: { opacity: 0 },
                    visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
                  }}
                >
                  {mainNavLinks.map((link) => (
                    <motion.div key={link.href} variants={fadeInUp}>
                      <button type="button" className={`text-base font-semibold transition-colors text-left ${isActive(link.href) ? 'text-foreground' : 'text-foreground/70 hover:text-foreground'}`} onClick={() => handleMobileNav(link.href)}>{t(link.labelKey)}</button>
                    </motion.div>
                  ))}

                  <motion.div variants={fadeInUp}>
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground/50 mt-3 mb-1">{t('nav.participation')}</p>
                  </motion.div>
                  {opportunityLinks.map((link) => (
                    <motion.div key={link.href} variants={fadeInUp} className="pl-3">
                      <button
                        type="button"
                        className={`text-base font-medium transition-colors text-left ${isActive(link.href) ? 'text-foreground' : 'text-foreground/70 hover:text-foreground'}`}
                        onClick={() => handleMobileNav(link.href)}
                      >
                        {t(link.labelKey)}
                      </button>
                    </motion.div>
                  ))}

                  <motion.div variants={fadeInUp}>
                    <button type="button" className={`text-base font-semibold transition-colors text-left ${isActive('/learn-more') ? 'text-foreground' : 'text-foreground/70 hover:text-foreground'}`} onClick={() => handleMobileNav('/learn-more')}>{t('nav.about')}</button>
                  </motion.div>

                  {/* Mobile Language Switcher */}
                  <motion.div variants={fadeInUp} className="mt-2">
                    <LanguageSwitcher variant="nav" />
                  </motion.div>

                  <motion.div variants={fadeInUp} className="mt-3">
                    <Button variant="outline" className="w-full rounded-full" onClick={() => handleMobileNav('/auth')}>{t('nav.log_in')}</Button>
                  </motion.div>
                  <motion.div variants={fadeInUp}>
                    <Button variant="luxury" className="w-full rounded-full h-11 text-sm font-semibold" onClick={() => handleMobileNav('/auth')}>{t('nav.join_amorette')}</Button>
                  </motion.div>
                  <motion.div variants={fadeInUp} className="pt-3 border-t border-border/20 mt-1">
                    <button
                      type="button"
                      className="text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors text-left"
                      onClick={() => handleMobileNav('/admin/login')}
                    >
                      Admin / Employee Login
                    </button>
                  </motion.div>
                </motion.nav>
              </SheetContent>
            </Sheet>
          </div>
        </motion.nav>
      </div>

      <main className="flex-1 pt-20">{children}</main>
      {showFooter && <PremiumCTA variant="premium-footer" />}
      {showFooter && <Footer />}
      <CookieConsent />
    </div>
  );
};

export default PublicLayout;
